/* Málaga: catálogo (colección y búsqueda).

   Todo por delegación: un listener por tipo de evento, instalado una sola vez sobre la
   sección. Los nodos que se reemplazan al filtrar no necesitan volver a "bindearse",
   que es lo que antes filtraba listeners (el selector de orden acumulaba uno por filtro).

   Filtros y orden se aplican juntos al enviar el formulario. La grilla crece con scroll
   infinito, pero el botón "Ver más" existe siempre: sin JS se navega normal. */

(() => {
  const root = document.querySelector('[data-collection]');
  if (!root) return;

  const sectionId = root.dataset.section;
  let controller = null;

  const panel = () => root.querySelector('[data-filter-panel]');
  const toggle = () => root.querySelector('[data-filter-toggle]');
  const grid = () => root.querySelector('[data-grid]');
  const results = () => root.querySelector('[data-results]');

  // ---------- Panel de filtros ----------
  const isMobile = () => window.matchMedia('(max-width: 1023px)').matches;

  const setPanel = (open) => {
    const p = panel();
    const t = toggle();
    if (!p || !t) return;
    p.toggleAttribute('data-open', open);
    t.setAttribute('aria-expanded', String(open));
    // En móvil el panel tapa todo: lo de atrás no debe ser tabulable
    const r = results();
    if (r) r.inert = open && isMobile();
    if (open) p.querySelector('input:not([type=hidden]), button, a')?.focus({ preventScroll: true });
    else t.focus({ preventScroll: true });
  };

  // ---------- Densidad de la grilla ----------
  // Se recuerda por tamaño de pantalla: 4 columnas en desktop no significa nada en un teléfono.
  const group = () => (window.matchMedia('(min-width: 1024px)').matches ? 'l' : 's');

  const applyCols = (cols) => {
    const g = grid();
    if (!g) return;
    g.dataset.cols = cols;
    root.querySelectorAll('[data-cols]').forEach((b) => {
      b.setAttribute('aria-pressed', String(b.dataset.cols === cols && b.dataset.colsGroup === group()));
    });
    // El srcset tiene que seguir a la densidad o las fotos quedan blandas
    const vw = Math.round(100 / Number(cols));
    g.querySelectorAll('img[srcset]').forEach((img) => {
      img.sizes = `${vw}vw`;
    });
    try {
      localStorage.setItem('malaga:cols-' + group(), cols);
    } catch (e) {
      /* sin storage: no pasa nada */
    }
  };

  const restoreCols = () => {
    try {
      const saved = localStorage.getItem('malaga:cols-' + group());
      if (saved && root.querySelector(`[data-cols="${saved}"][data-cols-group="${group()}"]`)) {
        applyCols(saved);
      }
    } catch (e) {
      /* ignore */
    }
  };

  restoreCols();
  window.matchMedia('(min-width: 1024px)').addEventListener('change', restoreCols);

  // ---------- Render (Section Rendering API) ----------
  const setLoading = (on) => {
    root.classList.toggle('is-loading', on);
    results()?.setAttribute('aria-busy', String(on));
  };

  const fetchSection = async (url, signal) => {
    const fetchUrl = new URL(url, location.origin);
    fetchUrl.searchParams.set('section_id', sectionId);
    const res = await fetch(fetchUrl, { signal });
    if (!res.ok) throw new Error(res.status);
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const fresh = doc.querySelector('[data-collection]');
    if (!fresh) throw new Error('sin seccion');
    return fresh;
  };

  const render = async (url, { push = true, scroll = false } = {}) => {
    controller?.abort();
    controller = new AbortController();
    // Invalida cualquier "ver más" en vuelo y corta el observador hasta tener la grilla nueva
    generation += 1;
    io?.disconnect();
    setLoading(true);
    try {
      const fresh = await fetchSection(url, controller.signal);
      const cols = grid()?.dataset.cols;

      ['[data-results]', '[data-panel-facets]', '[data-active-chips]', '[data-toolbar-count]', '[data-filter-count]'].forEach(
        (sel) => {
          const cur = root.querySelector(sel);
          const nxt = fresh.querySelector(sel);
          if (cur && nxt) cur.replaceWith(nxt);
        }
      );

      if (cols) applyCols(cols);
      autoLoaded = 0;
      observeSentinel();

      const clean = new URL(url, location.origin);
      clean.searchParams.delete('section_id');
      if (push) history.pushState({ malaga: true }, '', clean);
      if (scroll) window.scrollTo({ top: root.offsetTop, behavior: 'smooth' });
    } catch (err) {
      if (err.name === 'AbortError') return;
      location.assign(url);
      return;
    } finally {
      setLoading(false);
    }
  };

  const urlFromForm = (form) => {
    const params = new URLSearchParams();
    for (const [k, v] of new FormData(form)) {
      if (v !== '' && v != null) params.append(k, v);
    }
    // Cambiar un filtro siempre vuelve a la primera página
    params.delete('page');
    const base = form.getAttribute('action') || location.pathname;
    return `${base}?${params.toString()}`;
  };

  // ---------- Carga progresiva ----------
  // Después de 3 tandas automáticas pasa a requerir clic: si no, el footer nunca se alcanza.
  const AUTO_LIMIT = 3;
  let autoLoaded = 0;
  let loadingMore = false;
  let io = null;
  // Cada filtrado abre una "generación" nueva. Una carga de más productos que estaba en
  // vuelo cuando se filtró pertenece a la generación vieja y hay que descartarla: si no,
  // se agregan productos del filtro anterior debajo de los del filtro nuevo.
  let generation = 0;

  const loadMore = async () => {
    const more = root.querySelector('[data-more]');
    const link = root.querySelector('[data-load-more]');
    if (!link || loadingMore) return;
    const mine = generation;
    loadingMore = true;
    more?.classList.add('is-loading');
    try {
      const fresh = await fetchSection(link.getAttribute('href'));
      if (mine !== generation) return; // se filtró mientras cargaba: este lote ya no vale
      const g = grid();
      const freshItems = fresh.querySelectorAll('[data-grid] > li');
      if (g && freshItems.length) g.append(...freshItems);

      // El bloque de abajo trae el contador acumulado y el link a la página siguiente
      const nextMore = fresh.querySelector('[data-more]');
      if (more && nextMore) more.replaceWith(nextMore);
      else more?.remove();

      const cols = g?.dataset.cols;
      if (cols) applyCols(cols);

      const url = new URL(link.getAttribute('href'), location.origin);
      url.searchParams.delete('section_id');
      history.replaceState({ malaga: true }, '', url);

      observeSentinel();
    } catch (e) {
      more?.classList.remove('is-loading');
    } finally {
      loadingMore = false;
    }
  };

  const observeSentinel = () => {
    io?.disconnect();
    if (!('IntersectionObserver' in window)) return;
    const sentinel = root.querySelector('[data-sentinel]');
    if (!sentinel || autoLoaded >= AUTO_LIMIT) return;
    io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          autoLoaded += 1;
          io.disconnect();
          loadMore();
        }
      },
      { rootMargin: '600px' }
    );
    io.observe(sentinel);
  };

  observeSentinel();

  // ---------- Un listener por tipo de evento ----------
  root.addEventListener('click', (e) => {
    if (e.target.closest('[data-filter-toggle]')) {
      return setPanel(!panel()?.hasAttribute('data-open'));
    }
    if (e.target.closest('[data-filter-close]')) return setPanel(false);

    const dens = e.target.closest('[data-cols]');
    if (dens) return applyCols(dens.dataset.cols);

    if (e.target.closest('[data-load-more]')) {
      e.preventDefault();
      return loadMore();
    }

    const nav = e.target.closest('[data-facets-remove], [data-facets-clear]');
    if (nav) {
      e.preventDefault();
      setPanel(false);
      return render(nav.getAttribute('href'));
    }
  });

  root.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-facets]');
    if (!form) return;
    e.preventDefault();
    setPanel(false);
    render(urlFromForm(form), { scroll: true });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && panel()?.hasAttribute('data-open')) setPanel(false);
  });

  document.addEventListener('pointerdown', (e) => {
    const p = panel();
    if (!p?.hasAttribute('data-open')) return;
    if (p.contains(e.target) || e.target.closest('[data-filter-toggle]')) return;
    if (isMobile()) return; // en móvil el panel ocupa todo, no hay "afuera"
    setPanel(false);
  });

  window.addEventListener('popstate', () => {
    render(location.pathname + location.search, { push: false });
  });

  // ---------- Fila de subcategorías ----------
  const catnav = root.querySelector('[data-catnav]');
  if (catnav) {
    const track = catnav.querySelector('[data-catnav-track]');
    const prev = catnav.querySelector('[data-catnav-scroll="-1"]');
    const next = catnav.querySelector('[data-catnav-scroll="1"]');

    const syncArrows = () => {
      const max = track.scrollWidth - track.clientWidth;
      const overflows = max > 1;
      prev.hidden = !overflows || track.scrollLeft <= 1;
      next.hidden = !overflows || track.scrollLeft >= max - 1;
    };

    catnav.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-catnav-scroll]');
      if (!btn) return;
      track.scrollBy({ left: Number(btn.dataset.catnavScroll) * track.clientWidth * 0.8 });
    });

    // Traer la categoría activa a la vista, pero solo si de verdad quedó fuera.
    // Si la fila entra entera no se toca el scroll: si no, con la fuente todavía sin
    // cargar la fila mide de más y el primer ítem queda cortado.
    const revealActive = () => {
      const active = track.querySelector('[aria-current="page"]');
      if (!active || track.scrollWidth - track.clientWidth <= 1) return;
      // El destino se calcula con el scroll-padding del CSS: es el mismo valor al que
      // el scroll-snap va a realinear después, así que no se pelean entre sí.
      const pad = parseFloat(getComputedStyle(track).scrollPaddingInlineStart) || 0;
      const t = track.getBoundingClientRect();
      const a = active.getBoundingClientRect();
      if (a.left >= t.left + pad && a.right <= t.right) return;
      track.scrollLeft = Math.max(0, active.offsetLeft - pad);
    };

    const sync = () => {
      revealActive();
      syncArrows();
    };

    track.addEventListener('scroll', () => requestAnimationFrame(syncArrows), { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(sync).observe(track);
    else window.addEventListener('resize', sync);

    // Las fuentes cambian el ancho de la fila: recalcular cuando terminen de cargar
    document.fonts?.ready.then(sync);
    sync();
  }
})();

/* ---------- Carrusel de la tarjeta y favoritos ----------
   Van sobre document y no sobre la sección: las tarjetas también viven en la Home
   y en los relacionados, y sobreviven a cada re-render. */
(() => {
  const WISH_KEY = 'malaga:wishlist';

  const readWishlist = () => {
    try {
      return new Set(JSON.parse(localStorage.getItem(WISH_KEY) || '[]'));
    } catch (e) {
      return new Set();
    }
  };

  document.addEventListener('click', (e) => {
    const arrow = e.target.closest('[data-card-nav]');
    if (arrow) {
      const track = document.getElementById(arrow.getAttribute('aria-controls'));
      track?.scrollBy({ left: Number(arrow.dataset.cardNav) * track.clientWidth });
      return;
    }

    const heart = e.target.closest('[data-wish]');
    if (!heart) return;
    const on = heart.getAttribute('aria-pressed') !== 'true';
    heart.setAttribute('aria-pressed', String(on));
    try {
      const set = readWishlist();
      if (on) set.add(heart.dataset.wish);
      else set.delete(heart.dataset.wish);
      localStorage.setItem(WISH_KEY, JSON.stringify([...set]));
      document.dispatchEvent(new CustomEvent('malaga:wishlist', { detail: set.size }));
    } catch (err) {
      /* sin storage: el corazón cambia igual, pero no se guarda */
    }
  });

  // Marcar los guardados, también en las tarjetas que aparecen después
  const syncHearts = () => {
    const set = readWishlist();
    document.querySelectorAll('[data-wish]').forEach((b) => {
      b.setAttribute('aria-pressed', String(set.has(b.dataset.wish)));
    });
  };

  syncHearts();
  if ('MutationObserver' in window) {
    new MutationObserver(syncHearts).observe(document.body, { childList: true, subtree: true });
  }
})();
